import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { PropertyGalleryProps } from './PropertyDetail.types';

export function PropertyGallery({
  images,
  propertyName,
  fundedPercentage,
  currentIndex,
  onIndexChange,
  onPrevious,
  onNext,
}: PropertyGalleryProps) {
  return (
    <>
      {/* Main Image */}
      <div className="relative h-[280px] sm:h-[400px] lg:h-[500px] bg-[#F0F0F0]">
        <ImageWithFallback
          src={images[currentIndex]}
          alt={propertyName}
          className="w-full h-full object-cover"
        />

        {/* Image Navigation - Desktop */}
        {images.length > 1 && (
          <>
            <button
              onClick={onPrevious}
              aria-label="Previous image"
              className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border border-border items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[#242424]" />
            </button>
            <button
              onClick={onNext}
              aria-label="Next image"
              className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 border border-border items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-[#242424]" />
            </button>
          </>
        )}

        {/* Image Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full bg-white/90 border border-border">
          <span className="text-xs font-semibold text-[#242424]">{currentIndex + 1} / {images.length}</span>
        </div>

        {/* Funding Badge */}
        <div className="absolute top-4 left-4">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 border border-border">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-semibold text-[#242424]">{fundedPercentage}% Funded</span>
          </div>
        </div>
      </div>

      {/* Image Thumbnails - Desktop */}
      {images.length > 1 && (
        <div className="hidden md:block px-4 lg:px-0 mt-4">
          <div className="grid grid-cols-5 gap-3 max-w-5xl">
            {images.map((img, idx) => (
              <button
                key={`thumbnail-${idx}`}
                onClick={() => onIndexChange(idx)}
                aria-label={`View image ${idx + 1}`}
                aria-current={currentIndex === idx ? 'true' : undefined}
                className={`relative h-20 lg:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                  currentIndex === idx ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-border'
                }`}
              >
                <ImageWithFallback
                  src={img}
                  alt={`${propertyName} view ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
